import { withOktaAuth } from "@okta/okta-react";
import OktaSignInWidget from "@tailormed/common-client/component/OktaSignInWidget";
import { cancelSleepCheck } from "@tailormed/common-client/services/auth";
import { getAuthenticatedRedirectPath } from "@tailormed/common-client/util/deepLinkUtils";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { compose } from "redux";
import { useTranslation } from "react-i18next";

import { Col, Row, Typography, Spin } from "antd";
import LoginBackgroundIcon from "../assets/svg/LoginBackgroundIcon";
import LoginLogoIcon from "../assets/svg/LoginLogoIcon";
import { RED_GRAD } from "../constant/colors";
import { Routes } from "../constant/routes";

import { createStructuredSelector } from "reselect";
import { getLoginImagesAPI } from "../api/api";
import { oktaConfig } from "../constant/config";
import { selectIDPConfig } from "../store/selector";

const { Title } = Typography;

/**
 * Check if the current URL indicates we're returning from an IDP redirect.
 * Okta's IdP Discovery adds ?fromLogin=true when redirecting back after SSO.
 */
const isReturningFromIdp = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("fromLogin") === "true";
};
const LoginComponent = ({ authState, oktaAuth, redirectedToLogin, idpConfig }) => {
  const { t } = useTranslation();
  const [images, setImages] = useState({});
  const [isDefaultBackground, setIsDefaultBackground] = useState(false);

  const getImages = async () => {
    try {
      const result = await getLoginImagesAPI();
      if (result.data.success) {
        setImages(result.data.images);
        if (!result.data.images.clientLoginBackground) {
          setIsDefaultBackground(true);
        }
      } else {
        setIsDefaultBackground(true);
      }
    } catch (error) {
      setIsDefaultBackground(true);
      console.log(error);
    }
  };

  const onSuccess = (res) => {
    if (res.status === "SUCCESS") {
      if (!redirectedToLogin) {
        oktaAuth.setOriginalUri(Routes.ROOT);
      }

      return oktaAuth.signInWithRedirect();
    }
  };

  const onError = (err) => {
    console.log("error logging in", err);
  };

  useEffect(() => {
    getImages();
  }, []);

  useEffect(() => {
    // Clear any lingering local storage items
    // related to the handleReturnFromSleep check.
    // Leftover items can lead to unexpected logouts.
    cancelSleepCheck();
  }, []);

  // If already authenticated, redirect to the original URI (deep link) or ROOT
  if (authState?.isAuthenticated) {
    const redirectTo = getAuthenticatedRedirectPath(oktaAuth, Routes.ROOT);
    return <Redirect to={{ pathname: redirectTo }} />;
  }

  // Show loading state while authentication is pending or returning from IDP
  // This prevents the login form from flashing briefly before redirect
  // Only show loading for IDP return if authState hasn't been determined yet
  // to avoid infinite loading if SSO auth fails
  if (authState?.isPending || (isReturningFromIdp() && !authState)) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#fff"
        }}
      >
        <Spin size="large" />
        <div style={{ marginTop: 16, color: "#666", fontSize: 14 }}>Completing sign in...</div>
      </div>
    );
  }

  return (
    <Row>
      <Col span={6} style={{ display: "flex", flexDirection: "column", padding: 28 }}>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <LoginLogoIcon />
        </div>
        <Title style={{ color: RED_GRAD, paddingTop: 120, paddingLeft: 30, fontSize: "3.5em" }}>Welcome</Title>
        {idpConfig && (
          <OktaSignInWidget
            brandColor={RED_GRAD}
            oktaAuth={oktaAuth}
            oktaBaseUrl={oktaConfig.baseUrl}
            redirectUri={oktaConfig.redirectUri}
            requestContext={oktaConfig.requestContext}
            idpConfig={idpConfig}
            onSuccess={onSuccess}
            onError={onError}
            emailSupportLink={"mailto:support@tailormed.co"}
            emailSupportText={t("login_email_support")}
          />
        )}
      </Col>
      {isDefaultBackground ? (
        <Col span={18} style={{ backgroundColor: RED_GRAD }}>
          <div
            style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", paddingTop: 50 }}
          >
            <LoginBackgroundIcon />
          </div>
        </Col>
      ) : (
        <Col
          span={18}
          style={{
            backgroundImage: `url(${images.clientLoginBackground})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            height: "100vh"
          }}
        >
          <div style={{ backgroundColor: RED_GRAD, height: "100vh", width: "6px" }}></div>
          <img src={images.clientLogo} alt="" style={{ position: "absolute", top: "50px", right: "50px" }} />
        </Col>
      )}
    </Row>
  );
};

const mapStateToProps = createStructuredSelector({
  idpConfig: selectIDPConfig
});

const Login = compose(withOktaAuth, connect(mapStateToProps, null))(LoginComponent);

export { Login };
